/* istanbul ignore file */

//
// NOTE: I did test this with rendering, but the CI dips below 80, even though locally it passes.
// More work required here, for sure.
//

import _ from 'lodash'
import flatten from 'flat'
import { Quill } from 'react-quill'
import FuzzySet from 'fuzzyset.js'
import classnames from 'classnames/bind'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const KEYS = {
  TAB: 9,
  ENTER: 13,
  ESCAPE: 27,
  ARROW_UP: 38,
  ARROW_DOWN: 40,
  OPEN_BRACKET: 219,
}

// NOTE: I know we have React as a dep, but this is such a simple node, I don't mind keeping 10 lines around to
// construct what we need, instead of going through a full react partial renderer.
const node = (tag, attrs, ...children) => {
  const elem = document.createElement(tag)

  Object.keys(attrs).forEach(key => {
    elem.setAttribute(key, attrs[key])
  })

  // I know we don't have text nodes here, so just append without creating text nodes
  children.forEach(child => {
    if (typeof child === 'string') child = document.createTextNode(child)
    elem.appendChild(child)
  })

  return elem
}

const empty = node => {
  while (node.firstChild) {
    node.removeChild(node.firstChild)
  }
}

//
// - If the given data references are nested, we descend the completions on level at a time, to avoid
//   overwhelming the user with data choices.
// - Deals with focus events correctly.
// - A user can manually exit the suggestion by pressing `escape`.
// - Efficient tab behavior for completing templates. Tabbing down to an end node completes the selection.
//
export default class Templating {
  constructor(quill, props) {
    // Reference for later
    this.quill = quill

    this.suggestions = []
    this.rootSuggestions = []

    // NOTE: This isn't configurable right now, but can be made to be
    this.patternBegin = '{{'
    this.patternLength = this.patternBegin.length

    // Whether we are currently displaying suggestions
    this.open = false

    // Tracking this allows us to calculate the current query the user typed, and how to replace it with a suggestion
    this.replacePatternStartIndex = 0
    this.queryTextStartIndex = 0

    // These are all the state variables we require to track when the dropdown is open, and
    // which value is to be selected.
    this.container = null
    this.suggestionNodeList = []
    this.suggestionKeyList = []
    this.currentListPosition = 0

    // Tracks the currently selected template variables
    this.currentSelection = ''
    this.currentQuery = ''

    // The old bind trick
    this.onTextChange = this.renderVariableList.bind(this)
    // this.onSelectionChange = this.onSelectionChange.bind(this)
    this.onContainerOptionClicked = this.onContainerOptionClicked.bind(this)
    this.hideVariableSuggestions = this.hideVariableSuggestions.bind(this)
    this.setVariableSelection = this.setVariableSelection.bind(this)

    // Init default state
    this.createSuggestionContainer()

    // No need to bind if we have no template tag suggestions -- just ignore
    if (!_.isEmpty(props.suggestions)) {
      this.configureSuggestions(props.suggestions)
      this.bindKeyboardEvents()
    }
  }

  configureSuggestions(suggestions = []) {
    const suggestionsSet = new FuzzySet()

    if (!Array.isArray(suggestions)) {
      this.rootSuggestions = Object.keys(suggestions).map(s => [0, s])

      // I tried writing my own custom function for this, but this package looks like it does exactly what we need
      suggestions = Object.keys(flatten(suggestions))
    } else {
      this.rootSuggestions = suggestions
        .filter(s => !s.includes('.'))
        .map(s => [0, s])
    }

    // The goal here is to take a tree of the schema, map it to a flat list, and then add those
    // dot notation keys to the fuzzy set, so we can match the current query the user types with
    // the whole object.
    suggestions.forEach(nestedKey => {
      suggestionsSet.add(nestedKey)
    })

    this.suggestions = suggestionsSet
  }

  createSuggestionContainer() {
    this.container = node('div', {})

    // Thank you semantic!
    this.container.classList.add(
      'menu',
      'transition',
      cx('ql-module-templating-container')
    )
    this.container.style.position = 'absolute'

    this.quill.container.parentNode.appendChild(this.container)
  }

  // @docs - https://quilljs.com/docs/modules/keyboard/
  bindKeyboardEvents() {
    // For detecting when we need to show
    this.quill.keyboard.addBinding(
      {
        key: KEYS.OPEN_BRACKET,
        shiftKey: true,
      },
      ({ index, second }) => {
        // This index is where the cursor is currently at, which is a character _ahead_ of the last character, which is
        // why we go back an extra index over the pattern length
        const previousChar = this.quill
          .getText(index - this.patternLength + 1, 1)
          .trim()

        if (previousChar === '{') {
          this.showVariableSuggestions(index)
        }

        return true
      }
    )

    this.quill.keyboard.addBinding(
      { key: KEYS.ESCAPE },
      this.hideVariableSuggestions
    )

    this.quill.keyboard.addBinding(
      {
        key: KEYS.ARROW_DOWN,
      },
      this.handleListNavigation.bind(this, 'down')
    )

    this.quill.keyboard.addBinding(
      {
        key: KEYS.ARROW_UP,
      },
      this.handleListNavigation.bind(this, 'up')
    )

    // TAB to go further down auto select list (and automatically complete if at the end of a list
    this.quill.keyboard.addBinding({ key: KEYS.TAB }, this.setVariableSelection)

    this.quill.keyboard.addBinding(
      { key: KEYS.ENTER },
      this.setVariableSelection
    )

    // Quill already defines some handlers for actions with the tab key, so we need to put ours before theirs, because
    // they prevent further execution.
    const ourTabBinding = this.quill.keyboard.bindings[KEYS.TAB].pop()
    this.quill.keyboard.bindings[KEYS.TAB].unshift(ourTabBinding)

    const ourEnterBinding = this.quill.keyboard.bindings[KEYS.ENTER].pop()
    this.quill.keyboard.bindings[KEYS.ENTER].unshift(ourEnterBinding)
  }

  setVariableSelection(e) {
    // NOTE: Important to return true here, to keep the handlers firing
    if (!this.open) {
      return true
    }

    this.open = false

    if (this.currentSelection) {
      // Most of the time, this will be a selection through a keypress, but we can also complete via a click, in which
      // case we need to figure out where the current selection is
      const range = this.quill.getSelection()
      const position =
        (range && range.index) ||
        this.replacePatternStartIndex + 2 + this.currentQuery.length

      // Embed the new query selection into the input
      this.quill.insertEmbed(
        position,
        'variable',
        this.currentSelection,
        Quill.sources.USER
      )

      // Append some extra space so that we are now editing after the embed
      this.quill.setSelection(position + 1)

      // NOTE: Extremely important this comes _after_ the embed, since quill calculates the delta, and if it
      // comes before, it determines that the tag should be inside a new paragraph, which is not what we want.
      this.quill.deleteText(
        this.replacePatternStartIndex,
        position - this.replacePatternStartIndex,
        Quill.sources.USER
      )
    }

    this.hideVariableSuggestions()
  }

  //
  // TODO: Hook into our dropdown component to get consistent styles there
  //
  showVariableSuggestions(index) {
    this.replacePatternStartIndex = index - this.patternLength + 1
    this.queryTextStartIndex = index

    // Thank you quill! I didn't want to have to do this myself...
    const { left, top, height } = this.quill.getBounds(index)

    const toolbarModule = this.quill.getModule('toolbar')
    const toolbarHeight = toolbarModule
      ? toolbarModule.container.offsetHeight
      : 0

    // NOTE: I did try using the height of the toolbar in this calculation, but there were two main problems with that
    // approach. It would break if the toolbar module was disabled, which is entirely possible for pure templating, and
    // it assumes the toolbar only occupied a single line, and therefore would be equal to the line-height in the
    // editor.
    //
    // NOTE: This number is for the line-height in the editor plus some extra room. Can we grab a var in scss?
    this.container.style.top = top + height + toolbarHeight + 32 + 'px'
    this.container.style.left = left + 'px'
    this.container.addEventListener('click', this.onContainerOptionClicked)

    this.quill.on(Quill.events.TEXT_CHANGE, this.onTextChange)
    // this.quill.on(Quill.events.SELECTION_CHANGE, this.onSelectionChange)

    this.renderVariableList()
    this.open = true
  }

  //
  // Happens for a few reasons:
  // - User has forced the suggestions to end with 'escape' or 'space'
  // - A completion has been selected with the tab key
  //
  hideVariableSuggestions() {
    this.container.classList.remove('visible', 'display')
    this.container.removeEventListener('click', this.onContainerOptionClicked)

    empty(this.container)

    this.quill.off(Quill.events.TEXT_CHANGE, this.onTextChange)
    // this.quill.off(Quill.events.SELECTION_CHANGE, this.onSelectionChange)

    this.current = null

    this.open = false
    this.quill.focus()
  }

  /**
   * We use this to detect blur events that should close the suggestions
   *
   * TODO(Connor): This inteferes with the click handler for selection, so we omit it for now, and will come back to it.
   */
  // onSelectionChange(range, oldRange, source) {
  //   console.log('Selection had changed!!!', source)

  //   const rangeIndicatesBlur = range !== null && oldRange === null
  //   const domStateIndicatesBlur = !this.container.querySelector('*:focus')

  //   if (rangeIndicatesBlur || domStateIndicatesBlur) {
  //     this.hideVariableSuggestions()
  //   }

  //   return true
  // }

  /**
   * Responsible for tracking the current query once templating has started.
   *
   * NOTE: Debounce this bad boy if perf becomes a problem. I don't want to do it now because each key stroke
   * should feel like it is updating the list right away, and because this should be bounded by the speed in which
   * a user can enter input via a keypress.
   */
  renderVariableList() {
    if (!this.open) return

    // Check if the user has deleted text to before the template start character
    const { index } = this.quill.getSelection()
    if (this.queryTextStartIndex > index) {
      return this.hideVariableSuggestions()
    }

    this.currentQuery = this.quill.getText(
      this.queryTextStartIndex + 1,
      index - this.queryTextStartIndex - 1
    )

    // From some expirementing, we want to reduce the match probability here to get better quality suggestions
    const matchingSuggestions =
      this.suggestions.get(this.currentQuery, null, 0.2) || this.rootSuggestions

    empty(this.container)
    this.suggestionNodeList = []
    this.suggestionKeyList = []
    this.currentListPosition = 0
    this.container.scrollTop = 0

    for (const [i, match] of matchingSuggestions.entries()) {
      const attrs = {
        'data-key': i,
      }

      // First value is confidence level, which we do not care about, but we rely on to suggest the most likely match
      // at the top of the list
      const [, key] = match

      // Mark the first one as the current selection, either because:
      // 1) we are on the first level and need more input, or
      // 2) because we have ranked the current matches and the top is the strongest match
      if (i === this.currentListPosition) {
        this.currentSelection = key
        attrs.class = cx('ql-module-templating-variable-choice', 'is-current')
      } else {
        attrs.class = cx('ql-module-templating-variable-choice')
      }

      const listNode = node('div', attrs, key)
      this.container.appendChild(listNode)

      this.suggestionNodeList.push(listNode)
      this.suggestionKeyList.push(key)
    }

    // Show using a semantic class
    this.container.classList.add('visible', 'display')
  }

  handleListNavigation(action = 'down') {
    if (!this.open) {
      return true
    }

    let newSelection = null

    this.suggestionNodeList[this.currentListPosition].classList.remove(
      cx('is-current')
    )

    //
    // These actions are symmetrical:
    // - We bound the changes at either end, so we don't go below 0, and don't go higher than the final list element
    // - Update scroll position if we have to
    // - Change to the new selected element
    //
    // TODO: Test with lots of suggestion options to test overflow and scroll behavior
    //
    if (action === 'down') {
      this.currentListPosition = Math.min(
        this.suggestionNodeList.length - 1,
        this.currentListPosition + 1
      )

      newSelection = this.suggestionNodeList[this.currentListPosition]

      const scrollOffset = newSelection.offsetTop + newSelection.offsetHeight
      if (scrollOffset > this.container.offsetHeight) {
        this.container.scrollTop = scrollOffset - this.container.offsetHeight
      }
    } else if (action === 'up') {
      this.currentListPosition = Math.max(0, this.currentListPosition - 1)

      newSelection = this.suggestionNodeList[this.currentListPosition]

      const scrollOffset = newSelection.offsetTop
      if (
        this.container.offsetHeight <
        this.container.scrollHeight - scrollOffset
      ) {
        this.container.scrollTop = scrollOffset
      }
    }

    // Update UI state
    newSelection.classList.add(cx('is-current'))

    // Update data ready for completion
    this.currentSelection = this.suggestionKeyList[this.currentListPosition]
  }

  onContainerOptionClicked(e) {
    e.preventDefault()

    // Another way to select a value, through click selection
    if (e.target.parentNode === this.container) {
      const { key } = e.target.dataset
      this.currentSelection = this.suggestionKeyList[key]

      this.setVariableSelection()
    }
  }
}

Quill.register('modules/templating', Templating)
